import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, AlertCircle, ExternalLink, Loader2, ArrowRight } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import ThemedTable from "../../components/ThemedTable";
import Loader from "../../components/Loader";

const LedgerCollections = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/clients");
      setClients(res.data.clients || []);
    } catch (err) {
      console.error("Failed to fetch clients outstanding balances:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Format Currency Helper
  const fmt = (n) =>
    "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  // Format Date Helper
  const formatYMDtoDMY = (ymdStr) => {
    if (!ymdStr) return "N/A";
    const dateObj = new Date(ymdStr);
    if (isNaN(dateObj.getTime())) return "N/A";
    return `${String(dateObj.getDate()).padStart(2, "0")}-${String(
      dateObj.getMonth() + 1
    ).padStart(2, "0")}-${dateObj.getFullYear()}`;
  };

  // ─── Computed Values ───────────────────────────────────────────────────────

  const computedKPIs = useMemo(() => {
    const activeDebtors = clients.filter((c) => Number(c.balance || 0) > 0);
    const totalOutstanding = activeDebtors.reduce(
      (sum, c) => sum + Number(c.balance || 0),
      0
    );
    const highRiskOutstanding = activeDebtors
      .filter((c) => Number(c.balance || 0) > 15000)
      .reduce((sum, c) => sum + Number(c.balance || 0), 0);
    const highRiskCount = activeDebtors.filter(
      (c) => Number(c.balance || 0) > 15000
    ).length;

    const avgDebt = activeDebtors.length
      ? totalOutstanding / activeDebtors.length
      : 0;

    return {
      totalOutstanding,
      activeDebtorsCount: activeDebtors.length,
      highRiskOutstanding,
      highRiskCount,
      avgDebt,
    };
  }, [clients]);

  const filteredClients = useMemo(() => {
    return clients
      .filter((c) => Number(c.balance || 0) > 0)
      .filter((c) => {
        const nameMatch = c.name?.toLowerCase().includes(search.toLowerCase());
        const companyMatch = c.companyName?.toLowerCase().includes(search.toLowerCase());
        return nameMatch || companyMatch;
      })
      .filter((c) => {
        if (statusFilter === "all") return true;
        const isOverdue = Number(c.balance || 0) > 15000;
        if (statusFilter === "overdue") return isOverdue;
        if (statusFilter === "active") return !isOverdue;
        return true;
      })
      .map((c) => {
        const isOverdue = Number(c.balance || 0) > 15000;
        return {
          ...c,
          formattedLastOrder: formatYMDtoDMY(c.lastOrderedDate),
          status: isOverdue ? "Overdue" : "Active",
        };
      })
      .sort((a, b) => b.balance - a.balance); // Highest outstanding balance first
  }, [clients, search, statusFilter]);

  return (
    <div className="bg-slate-50 min-h-screen text-sm">
      {/* ── HEADER ── */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Ledger & Collections</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage B2B outstanding balances, debt recovery, and ledgers
          </p>
        </div>
      </div>

      {loading && clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-40">
          <Loader size={36} />
          <p className="text-sm text-slate-400 mt-4">Retrieving client accounts...</p>
        </div>
      ) : (
        <>
          {/* ── KPI CARDS (All-Time style) ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {/* Total Outstanding */}
            <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-red-500 to-rose-600 border-e border-b">
              <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
              <p className="text-xs opacity-80 relative z-10 font-semibold uppercase tracking-wider">
                Total Outstanding
              </p>
              <h2 className="text-2xl font-bold mt-2 relative z-10">
                {fmt(computedKPIs.totalOutstanding)}
              </h2>
              <p className="text-xs opacity-80 mt-1 relative z-10 font-medium">
                Total pending collection
              </p>
            </div>

            {/* Active Debtors count */}
            <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-e border-b">
              <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
              <p className="text-xs opacity-80 relative z-10 font-semibold uppercase tracking-wider">
                Active Debtors
              </p>
              <h2 className="text-2xl font-bold mt-2 relative z-10">
                {computedKPIs.activeDebtorsCount} Accounts
              </h2>
              <p className="text-xs opacity-80 mt-1 relative z-10 font-medium">
                Clients with unpaid balances
              </p>
            </div>

            {/* High Risk Credit */}
            <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-e border-b">
              <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
              <p className="text-xs opacity-80 relative z-10 font-semibold uppercase tracking-wider">
                High Risk Credit
              </p>
              <h2 className="text-2xl font-bold mt-2 relative z-10">
                {fmt(computedKPIs.highRiskOutstanding)}
              </h2>
              <p className="text-xs opacity-80 mt-1 relative z-10 font-medium">
                Owed by {computedKPIs.highRiskCount} overdue accounts (&gt;15k)
              </p>
            </div>

            {/* Avg Debt per Client */}
            <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 border-b">
              <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
              <p className="text-xs opacity-80 relative z-10 font-semibold uppercase tracking-wider">
                Avg Debt Per Client
              </p>
              <h2 className="text-2xl font-bold mt-2 relative z-10">
                {fmt(computedKPIs.avgDebt)}
              </h2>
              <p className="text-xs opacity-80 mt-1 relative z-10 font-medium">
                Average balance per debtor
              </p>
            </div>
          </div>

          {/* ── FILTER & SEARCH BAR ── */}
          <div className="border-t bg-[#EFF6FF] p-3">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search input */}
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search size={18} className="text-slate-400" />
                </span>
                <input
                  type="text"
                  placeholder="Search by client or company name..."
                  className="pl-10 pr-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Status filter dropdown */}
              <div className="w-full sm:w-48">
                <select
                  className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Debtors</option>
                  <option value="overdue">Overdue (&gt; ₹15,000)</option>
                  <option value="active">Active (&lt; ₹15,000)</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── CLIENT OUTSTANDING TABLE ── */}
          <div className="border border-slate-200 rounded-none bg-white">
            <div className="overflow-x-auto">
              <ThemedTable className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-white border-b border-slate-200">
                    <th className="py-3 px-5 text-xs uppercase tracking-wider text-slate-500">
                      Client details
                    </th>
                    <th className="py-3 px-5 text-xs uppercase tracking-wider text-slate-500 text-right">
                      Outstanding balance
                    </th>
                    <th className="py-3 px-5 text-xs uppercase tracking-wider text-slate-500">
                      Last transaction
                    </th>
                    <th className="py-3 px-5 text-xs uppercase tracking-wider text-slate-500 text-center">
                      Status
                    </th>
                    <th className="py-3 px-5 text-xs uppercase tracking-wider text-slate-500 text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-slate-400">
                        No clients found matching the filters.
                      </td>
                    </tr>
                  ) : (
                    filteredClients.map((client, idx) => (
                      <tr key={client._id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        {/* Client details */}
                        <td className="py-3.5 px-5">
                          <div className="font-semibold text-slate-800">
                            {client.name || "Unknown"}
                          </div>
                          {client.companyName && (
                            <div className="text-xs text-slate-400 mt-0.5">
                              {client.companyName}
                            </div>
                          )}
                        </td>

                        {/* Balance */}
                        <td className="py-3.5 px-5 text-right">
                          <span className="font-bold text-red-600">
                            {fmt(client.balance)}
                          </span>
                        </td>

                        {/* Last ordered date */}
                        <td className="py-3.5 px-5 text-slate-600">
                          {client.formattedLastOrder}
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-5 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                              client.status === "Overdue"
                                ? "bg-red-100 text-red-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            <AlertCircle size={10} />
                            {client.status}
                          </span>
                        </td>

                        {/* Action buttons */}
                        <td className="py-3.5 px-5 text-center">
                          <button
                            onClick={() =>
                              navigate(`/manage-client/client-ledger/${client._id}`)
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-900 text-white hover:bg-blue-800 text-xs font-semibold rounded transition"
                          >
                            <span>View Ledger</span>
                            <ExternalLink size={12} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </ThemedTable>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LedgerCollections;
